import type { AdminFeedbackListItemResponse } from "../../../types/adminTypes";
import { formatDateTime } from "../../../utils/DateUtils";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";
import { getFeedbackRoleLabel, getFeedbackTypeLabel } from "./adminFeedbackUtils";

type AdminFeedbackDetailModalProps = {
  feedback: AdminFeedbackListItemResponse | null;
  isOpen: boolean;
  onClose: () => void;
};

const AdminFeedbackDetailModal = ({ feedback, isOpen, onClose }: AdminFeedbackDetailModalProps) => {
  if (!feedback) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Feedback detail" panelClassName="max-w-3xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <AdminPillBadge>{getFeedbackTypeLabel(feedback.type)}</AdminPillBadge>
          <AdminPillBadge>{getFeedbackRoleLabel(feedback.respondentRole)}</AdminPillBadge>
          {feedback.rating !== null ? <AdminPillBadge tone="success">{feedback.rating} / 5</AdminPillBadge> : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Submitted</p>
            <p className="mt-1 text-sm text-myteengig-text-primary">{formatDateTime(feedback.createdAt)}</p>
          </div>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Email</p>
            <p className="mt-1 break-all text-sm text-myteengig-text-primary">{feedback.email}</p>
          </div>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Gig</p>
            <p className="mt-1 text-sm text-myteengig-text-primary">{feedback.gigTitle ?? "Not tied to gig"}</p>
          </div>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Source path</p>
            <p className="mt-1 break-all text-sm text-myteengig-text-primary">{feedback.sourcePath ?? "Not provided"}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Message</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">{feedback.message}</p>
        </div>

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Feedback ID</p>
            <p className="mt-1 break-all text-myteengig-text-primary">{feedback.id}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">User ID</p>
            <p className="mt-1 break-all text-myteengig-text-primary">{feedback.userId ?? "Anonymous"}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Gig request ID</p>
            <p className="mt-1 break-all text-myteengig-text-primary">{feedback.gigRequestId ?? "Not tied to gig"}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Gig ID</p>
            <p className="mt-1 break-all text-myteengig-text-primary">{feedback.gigId ?? "Not tied to gig"}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AdminFeedbackDetailModal;
